import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface Visitor {
  id: string;
  visitorName: string;
  visitorCompany: string;
  visitorContact: string;
  visitorEmail: string;
  contactPerson: string;
  groupSize: number;
  isGroup: boolean;
  timestamp: string;
}

const QuickLookup: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVisitors = async () => {
      try {
        setIsLoading(true);
        
        const cachedData = localStorage.getItem('visitorsData');
        if (cachedData) {
          const parsedData = JSON.parse(cachedData);
          setVisitors(parsedData.visitors || []);
          setIsLoading(false);
        }
        
        const response = await fetch('https://1s916i26yc.execute-api.eu-west-1.amazonaws.com/api/get-visitors');
        if (!response.ok) {
          throw new Error(`Failed to fetch visitors: ${response.statusText}`);
        }
        
        const data = await response.json();
        setVisitors(data.visitors || []);
        
        localStorage.setItem('visitorsData', JSON.stringify(data));
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching visitors:', error);
        setError('Failed to load visitor data. Please try again later.');
        setIsLoading(false);
      }
    };
    
    fetchVisitors();
  }, []);

  useEffect(() => {
    if (searchTerm.trim().length >= 2) {
      const searchTermLower = searchTerm.toLowerCase();
      const filteredVisitors = visitors.filter(visitor => 
        visitor.visitorName.toLowerCase().includes(searchTermLower) ||
        visitor.visitorCompany.toLowerCase().includes(searchTermLower)
      );
      setVisitors(filteredVisitors);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
      if (searchTerm.trim().length === 0) {
        const cachedData = localStorage.getItem('visitorsData');
        if (cachedData) {
          const parsedData = JSON.parse(cachedData);
          setVisitors(parsedData.visitors || []);
        }
      }
    }
  }, [searchTerm]);

  const handleSelectVisitor = (visitor: Visitor) => {
    localStorage.setItem('selectedVisitor', JSON.stringify({
      visitorName: visitor.visitorName,
      visitorCompany: visitor.visitorCompany,
      visitorContact: visitor.visitorContact,
      visitorEmail: visitor.visitorEmail,
      isGroup: false,
      rememberMe: true
    }));
    
    sessionStorage.setItem('fromLookup', 'true');
    
    navigate('/visitor-sign-in');
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="quick-lookup-container">
      <div className="video-overlay"></div>
      <video className="background-video" autoPlay loop muted playsInline>
        <source
          src="https://redrocket.energy/wp-content/uploads/2024/05/RedRocketAnimation1200p_Loop.mp4"
          type="video/mp4"
        />
        Your browser does not support the video tag.
      </video>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="lookup-content"
      >
        <motion.button 
          className="back-button"
          onClick={handleBack}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <path 
              d="M19 12H5M5 12L12 19M5 12L12 5" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
          Back to Home
        </motion.button>

        <div className="lookup-header">
          <motion.img 
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            src="https://redrocket.energy/wp-content/uploads/2021/03/rr-emblem.svg" 
            alt="Red Rocket Logo" 
            className="logo" 
          />
          <h1>Returning Visitor</h1>
          <p className="subtitle">Find your previous registration to pre-fill the form</p>
        </div>

        <div className="search-container">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
            onFocus={() => {
              if (searchTerm.trim().length >= 2) {
                setShowSuggestions(true);
              }
            }}
            placeholder="Search by name or company..."
            className="search-input"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
          />
          
          {isLoading && (
            <div className="loading-indicator">Loading visitor data...</div>
          )}
          
          {error && (
            <div className="error-message">{error}</div>
          )}
          
          <AnimatePresence>
            {showSuggestions && visitors.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="suggestions-dropdown"
              >
                {visitors.map((visitor) => (
                  <motion.div
                    key={visitor.id}
                    className="suggestion-item"
                    onClick={() => handleSelectVisitor(visitor)}
                    whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                  >
                    {visitor.visitorName} - {visitor.visitorCompany}
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default QuickLookup; 